import { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Settings2, HelpCircle, RotateCcw, Crosshair, SlidersHorizontal, Disc3, Wrench, Smartphone, Radio, Crosshair as Target, Mic, MicOff,
} from "lucide-react";
import { useMeasurements } from "../context/MeasurementsContext";
import { useVehicle } from "../context/VehicleContext";
import { useUnits } from "../context/UnitsContext";
import { useVoiceCapture } from "../hooks/useVoiceCapture";
import { AILevel } from "../components/AILevel";
import { wheelLean, rafThrottle, startTilt, NO_SENSOR_HELP } from "../lib/tilt";

const num = (v) => (v === "" || v === null || v === undefined || Number.isNaN(Number(v)) ? null : Number(v));
const C_GREEN = "#10B981", C_RED = "#EF4444", C_AMBER = "#F59E0B", C_CYAN = "#06B6D4", C_SLATE = "#64748B", C_YELLOW = "#FACC15";

function statusColor(v, spec) {
  if (v === null) return C_SLATE;
  if (v < spec.min || v > spec.max) return C_RED;
  const edge = (spec.max - spec.min) * 0.15;
  if (v < spec.min + edge || v > spec.max - edge) return C_AMBER;
  return C_GREEN;
}
const fmt = (v) => (v === null ? "--" : `${v >= 0 ? "" : ""}${v.toFixed(1)}°`);

// Small semicircle arc gauge with a marker dot
function ArcGauge({ value, spec }) {
  const w = 96, h = 46, cx = w / 2, cy = h - 4, r = 36;
  const col = statusColor(value, spec);
  let t = value === null ? 0.5 : (value - spec.min) / (spec.max - spec.min);
  t = Math.max(0, Math.min(1, t));
  const ang = Math.PI - t * Math.PI; // 180deg -> 0deg
  const mx = cx + r * Math.cos(ang);
  const my = cy - r * Math.sin(ang);
  const track = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="mx-auto">
      <path d={track} fill="none" stroke="#243049" strokeWidth="4" strokeLinecap="round" />
      <path d={track} fill="none" stroke={C_YELLOW} strokeWidth="4" strokeLinecap="round"
        strokeDasharray={`${t * Math.PI * r} ${Math.PI * r}`} opacity="0.55" />
      {value !== null && <circle cx={mx} cy={my} r="5.5" fill={col} stroke="#070B14" strokeWidth="2" />}
    </svg>
  );
}

// Stylized tire; rotation is drawn inside the SVG (attribute transform) so every browser repaints it
function Tire({ camber = 0, tiltDeg, small, live }) {
  const tilt = tiltDeg !== undefined ? Math.max(-22, Math.min(22, tiltDeg)) : Math.max(-18, Math.min(18, (camber || 0) * 6));
  const wd = small ? 62 : 74, ht = small ? 104 : 118;
  return (
    <svg width={wd} height={ht} viewBox="-12 -2 84 124" className="mx-auto block" data-testid="qa-tire-svg" data-tilt={tilt.toFixed(1)}>
      <g transform={`rotate(${tilt.toFixed(2)} 30 60)`}>
        <rect x="10" y="6" width="40" height="108" rx="16" fill="#0E1526" stroke={C_YELLOW} strokeOpacity={live ? 1 : 0.85} strokeWidth={live ? 3.5 : 2.5} />
        <rect x="18" y="14" width="24" height="92" rx="10" fill="#070B14" stroke={C_YELLOW} strokeOpacity="0.35" strokeWidth="1.5" />
        {[24, 40, 56, 72, 88].map((y) => (
          <line key={y} x1="20" y1={y} x2="40" y2={y} stroke={C_YELLOW} strokeOpacity="0.55" strokeWidth="2" />
        ))}
        {live && <line x1="30" y1="0" x2="30" y2="120" stroke={C_YELLOW} strokeWidth="2" />}
      </g>
      {live && <line x1="30" y1="-2" x2="30" y2="122" stroke="#ffffff" strokeOpacity="0.7" strokeWidth="1.5" strokeDasharray="4 4" data-testid="qa-plumb-line" />}
    </svg>
  );
}

function RangeBar({ value, spec, testId, isToe }) {
  const { toeNum, toeSuffix } = useUnits();
  const col = statusColor(value, spec);
  let t = value === null ? 0.5 : (value - spec.min) / (spec.max - spec.min);
  t = Math.max(0, Math.min(1, t));
  const lo = isToe ? `${toeNum(spec.min)}${toeSuffix}` : `${spec.min.toFixed(2)}°`;
  const hi = isToe ? `${toeNum(spec.max)}${toeSuffix}` : `${spec.max >= 0 ? "+" : ""}${spec.max.toFixed(2)}°`;
  return (
    <div className="w-full" data-testid={testId}>
      <div className="relative h-2 rounded-full bg-steel">
        <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${t * 100}%`, background: C_CYAN, opacity: 0.5 }} />
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full border-2 border-ink"
          style={{ left: `${t * 100}%`, background: col }} />
      </div>
      <div className="flex justify-between font-mono text-[10px] text-slate-500 mt-1">
        <span>{lo}</span>
        <span>{hi}</span>
      </div>
    </div>
  );
}

function LevelDiamond({ aligned }) {
  const col = aligned ? C_GREEN : C_AMBER;
  return (
    <svg width="64" height="86" viewBox="0 0 64 86" className="mx-auto">
      <path d="M12 14 L32 2 L52 14" fill="none" stroke="#334155" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 72 L32 84 L52 72" fill="none" stroke="#334155" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M32 28 L54 43 L32 58 L10 43 Z" fill={col} fillOpacity="0.9" stroke={col} strokeWidth="2"
        style={{ filter: `drop-shadow(0 0 10px ${col}88)` }} />
      <path d="M22 43 L30 49 L44 36" fill="none" stroke="#070B14" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const BottomTab = ({ icon: Icon, label, active, onClick, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className={`flex-1 flex flex-col items-center gap-1 py-1.5 transition-colors ${active ? "text-cyan" : "text-slate-500 hover:text-slate-300"}`}>
    <Icon size={20} />
    <span className="font-mono text-[10px] tracking-wide">{label}</span>
  </button>
);

export default function QuickAlign() {
  const { m, reset, setWheel } = useMeasurements();
  const { specs } = useVehicle();
  const { fmtToe } = useUnits();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [view, setView] = useState("toe"); // toe | camber
  const [checkMode, setCheckMode] = useState("6"); // 6 | 4

  // ---- live lean sensor (device orientation) ----
  const [live, setLive] = useState(false);
  const [activeCorner, setActiveCorner] = useState(null);
  const [raw, setRaw] = useState(null);
  const smoothRef = useRef(null);
  const stopTiltRef = useRef(null);
  const CORNER_FIELD = { fl: "frontLeft", fr: "frontRight", rl: "rearLeft", rr: "rearRight" };

  const handleOrientation = useMemo(() => rafThrottle((e) => {
    const g = wheelLean(e);
    if (g == null) return;
    smoothRef.current = smoothRef.current == null ? g : smoothRef.current * 0.82 + g * 0.18;
    setRaw(Math.round(smoothRef.current * 10) / 10);
  }), []);

  useEffect(() => () => { stopTiltRef.current?.(); handleOrientation.cancel(); }, [handleOrientation]);

  const toggleLive = async () => {
    if (live) {
      stopTiltRef.current?.(); stopTiltRef.current = null;
      handleOrientation.cancel();
      setLive(false); setActiveCorner(null); setRaw(null); smoothRef.current = null;
      return;
    }
    try {
      if (typeof DeviceOrientationEvent !== "undefined" && typeof DeviceOrientationEvent.requestPermission === "function") {
        const res = await DeviceOrientationEvent.requestPermission();
        if (res !== "granted") { toast.error("Motion sensor permission denied"); return; }
      }
      if (typeof DeviceOrientationEvent === "undefined") { toast.error("Motion sensor not available on this device"); return; }
      stopTiltRef.current?.();
      stopTiltRef.current = startTilt(handleOrientation, () => toast.error(NO_SENSOR_HELP.title, NO_SENSOR_HELP));
      setLive(true);
      setActiveCorner((c) => c || "fl");
      setView("camber");
      toast.success("Live lean active", { description: "Tap a wheel, then lean the phone on that tire." });
    } catch {
      toast.error("Could not start the motion sensor");
    }
  };

  const liveCam = raw == null ? null : Math.round(raw * 10) / 10;
  const captureCorner = () => {
    if (!activeCorner || liveCam == null) return;
    setWheel(CORNER_FIELD[activeCorner], "camber", liveCam);
    toast.success(`Captured ${activeCorner.toUpperCase()} camber`, { description: `${liveCam >= 0 ? "+" : ""}${liveCam.toFixed(1)}°` });
  };

  // ---- hands-free voice capture (say "capture") ----
  const voice = useVoiceCapture(captureCorner, { keywords: ["capture", "lock it", "lock"] });
  const toggleVoice = () => {
    if (!voice.supported) {
      toast.error("Voice capture isn't supported on this browser", {
        description: "Works best in Chrome / Android. You can still tap Capture.",
      });
      return;
    }
    if (voice.listening) {
      voice.stop();
      toast.message("Voice capture off");
    } else {
      voice.start();
      toast.success("Voice capture on", { description: 'Say "capture" to lock the selected corner.' });
    }
  };
  // Stop listening whenever live lean is turned off.
  useEffect(() => {
    if (!live && voice.listening) voice.stop();
  }, [live, voice]);

  const frontToe = num(m.frontToe);
  const rearToe = num(m.rearToe);
  const flCam = num(m.frontLeft?.camber), frCam = num(m.frontRight?.camber);
  const rlCam = num(m.rearLeft?.camber), rrCam = num(m.rearRight?.camber);

  const toeFrontSpec = specs.front.totalToe;
  const toeRearSpec = specs.rear.totalToe;
  const camSpec = specs.front.camber;
  const camRearSpec = specs.rear.camber;
  // per-wheel toe ~ half of axle total toe
  const halfSpec = (s) => ({ min: s.min / 2, max: s.max / 2, target: s.target / 2 });
  const toeWheelSpec = halfSpec(toeFrontSpec);

  const isCamber = view === "camber";
  const showVal = (v) => (isCamber ? fmt(v) : fmtToe(v, false));

  // top wheels
  const topLeft = isCamber ? { v: flCam, spec: camSpec, label: "FL Camber", corner: "fl" } : { v: frontToe !== null ? frontToe / 2 : null, spec: toeWheelSpec, label: "FL Toe", corner: "fl" };
  const topRight = isCamber ? { v: frCam, spec: camSpec, label: "FR Camber", corner: "fr" } : { v: frontToe !== null ? frontToe / 2 : null, spec: toeWheelSpec, label: "FR Toe", corner: "fr" };
  const botLeft = isCamber ? { v: rlCam, spec: camRearSpec, label: "RL Camber", corner: "rl" } : { v: rearToe !== null ? rearToe / 2 : null, spec: halfSpec(toeRearSpec), label: "RL Toe", corner: "rl" };
  const botRight = isCamber ? { v: rrCam, spec: camRearSpec, label: "RR Camber", corner: "rr" } : { v: rearToe !== null ? rearToe / 2 : null, spec: halfSpec(toeRearSpec), label: "RR Toe", corner: "rr" };

  const avg = (a, b) => (a === null && b === null ? null : ((a ?? 0) + (b ?? 0)) / ((a !== null) + (b !== null) || 1));
  const frontBar = isCamber ? { v: avg(flCam, frCam), spec: camSpec, label: "Front Camber" } : { v: frontToe, spec: toeFrontSpec, label: "Total Toe" };
  const rearBar = isCamber ? { v: avg(rlCam, rrCam), spec: camRearSpec, label: "Rear Camber" } : { v: rearToe, spec: toeRearSpec, label: "Total Toe" };

  const aligned = useMemo(() => {
    const inSpec = (v, s) => v !== null && v >= s.min && v <= s.max;
    return inSpec(frontBar.v, frontBar.spec) && inSpec(rearBar.v, rearBar.spec);
  }, [frontBar, rearBar]);

  const handleRestart = () => { reset(); toast("Readings cleared"); };

  const renderWheel = (data, small) => {
    const liveActive = live && activeCorner === data.corner;
    const v = liveActive ? liveCam : data.v;
    const selectable = live && data.corner;
    const tiltDeg = v === null ? 0
      : liveActive ? 22 * Math.tanh(v / 1.5)
      : (isCamber ? Math.max(-22, Math.min(22, v * 6)) : Math.max(-22, Math.min(22, v * 45)));
    return (
      <button type="button" key={data.corner} disabled={!selectable} onClick={() => selectable && setActiveCorner(data.corner)}
        data-testid={`qa-wheel-${data.corner}`}
        className={`flex flex-col items-center gap-1 rounded-2xl p-2 transition-all ${selectable ? "cursor-pointer" : "cursor-default"} ${liveActive ? "ring-2 ring-yellow-400 bg-yellow-400/5" : selectable ? "hover:bg-elevated/40" : ""}`}>
        <div className="text-center">
          <span className="font-body text-xs text-slate-300">{data.label}: </span>
          <span className="font-body text-xs font-bold" style={{ color: statusColor(v, data.spec) }}>{showVal(v)}</span>
        </div>
        <ArcGauge value={v} spec={data.spec} />
        <div data-testid={`qa-tire-${data.corner}`}>
          <Tire tiltDeg={tiltDeg} small={small} live={liveActive} />
        </div>
        {liveActive && <span className="font-mono text-[8px] text-slate-500 tracking-widest uppercase">tilt exaggerated</span>}
        {liveActive && <span className="font-mono text-[9px] text-yellow-400 tracking-widest uppercase">● Live</span>}
      </button>
    );
  };

  return (
    <div className="max-w-md mx-auto px-4 pb-28 pt-4 animate-rise">
      {/* header */}
      <div className="flex items-center justify-between mb-5">
        <button data-testid="qa-live-toggle" onClick={toggleLive}
          className={`w-10 h-10 rounded-full border flex items-center justify-center transition-colors ${live ? "bg-cyan text-ink border-cyan animate-pulse" : "bg-elevated border-steel text-slate-300 hover:text-cyan"}`}
          title="Live lean sensor">
          <Radio size={18} />
        </button>
        <div className="text-center">
          <div className="flex items-center gap-2 justify-center">
            <div className="flex items-center justify-center w-6 h-6 rounded-md bg-gradient-to-br from-cyan to-blue-600 text-ink font-heading font-black text-[10px]">TK</div>
            <span className="font-heading font-extrabold uppercase tracking-wide text-lg">Thortek</span>
          </div>
          <div className="font-body text-xs text-slate-400 mt-0.5">{live ? t("quick.liveSub") : isCamber ? t("quick.camberSub") : t("quick.toeSub")}</div>
        </div>
        <button data-testid="qa-gauge" onClick={() => navigate("/gauge")}
          className="w-10 h-10 rounded-full bg-elevated border border-steel flex items-center justify-center text-cyan hover:text-white transition-colors" title="Lean-on tilt gauge">
          <Smartphone size={18} />
        </button>
      </div>

      <AILevel paused={live} />

      {/* live lean banner */}
      {live && (
        <div data-testid="qa-live-bar" className="mb-4 rounded-xl border border-cyan/40 bg-cyan/5 p-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-mono text-[10px] text-cyan tracking-widest uppercase">
              {activeCorner ? `${activeCorner.toUpperCase()} · ${t("quick.liveLean")}` : t("quick.liveTap")}
            </div>
            <div className="font-heading text-xl font-bold tabular-nums" style={{ color: activeCorner ? C_CYAN : C_SLATE }}>
              {activeCorner && liveCam != null ? `${liveCam >= 0 ? "+" : ""}${liveCam.toFixed(1)}°` : "--"}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button data-testid="qa-voice-toggle" onClick={toggleVoice}
              aria-label="Toggle voice capture"
              className={`inline-flex items-center justify-center rounded-xl w-11 h-11 border transition-all ${
                voice.listening
                  ? "border-cyan bg-cyan/20 text-cyan animate-pulse"
                  : "border-white/15 text-slate-300 hover:border-cyan/50 hover:text-cyan"
              }`}>
              {voice.listening ? <Mic size={18} /> : <MicOff size={18} />}
            </button>
            <button data-testid="qa-capture" onClick={captureCorner} disabled={!activeCorner || liveCam == null}
              className="inline-flex items-center gap-2 rounded-xl bg-cyan text-ink px-4 py-2.5 font-mono text-xs uppercase tracking-widest font-bold disabled:opacity-40">
              <Target size={15} /> {t("common.capture")}
            </button>
          </div>
        </div>
      )}
      {live && activeCorner && liveCam != null && Math.abs(liveCam) > 12 && (
        <div data-testid="qa-hold-hint" className="-mt-2 mb-4 rounded-lg bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-center font-body text-xs text-amber-300">
          Reading {Math.abs(liveCam).toFixed(0)}° — make sure the phone is sitting flat against the tire.
        </div>
      )}
      {live && voice.listening && (
        <div data-testid="qa-voice-hint" className="-mt-2 mb-4 text-center font-mono text-[10px] uppercase tracking-widest text-cyan">
          Listening — say "capture"
        </div>
      )}

      {/* front wheels + arcs */}
      <div className="grid grid-cols-2 gap-10">
        {renderWheel(topLeft)}
        {renderWheel(topRight)}
      </div>

      {/* front total bar */}
      <div className="text-center mt-4">
        <span className="font-body text-sm text-slate-300">{frontBar.label}: </span>
        <span className="font-heading text-sm font-bold" style={{ color: statusColor(frontBar.v, frontBar.spec) }}>{showVal(frontBar.v)}</span>
      </div>
      <div className="px-6 mt-2"><RangeBar value={frontBar.v} spec={frontBar.spec} testId="qa-front-bar" isToe={!isCamber} /></div>

      {/* center level */}
      <div className="my-5"><LevelDiamond aligned={aligned} /></div>

      {/* rear total bar */}
      <div className="text-center">
        <span className="font-body text-sm text-slate-300">{rearBar.label}: </span>
        <span className="font-heading text-sm font-bold" style={{ color: statusColor(rearBar.v, rearBar.spec) }}>{showVal(rearBar.v)}</span>
      </div>
      <div className="px-6 mt-2 mb-4"><RangeBar value={rearBar.v} spec={rearBar.spec} testId="qa-rear-bar" isToe={!isCamber} /></div>

      {/* rear wheels */}
      <div className="grid grid-cols-2 gap-10">
        {isCamber || checkMode === "6" ? renderWheel(botLeft, true) : <div className="flex justify-center"><Tire small /></div>}
        {isCamber || checkMode === "6" ? renderWheel(botRight, true) : <div className="flex justify-center"><Tire small /></div>}
      </div>

      {/* check mode toggle */}
      <div className="flex items-center justify-center gap-3 mt-8">
        <button data-testid="qa-6point" onClick={() => setCheckMode("6")}
          className={`px-4 py-2 rounded-lg font-mono text-xs tracking-wide border transition-colors ${checkMode === "6" ? "bg-elevated border-cyan text-white" : "border-steel text-slate-400 hover:text-white"}`}>
          6-Point Check
        </button>
        <button data-testid="qa-4point" onClick={() => setCheckMode("4")}
          className={`px-4 py-2 rounded-lg font-mono text-xs tracking-wide border transition-colors ${checkMode === "4" ? "bg-elevated border-cyan text-white" : "border-steel text-slate-400 hover:text-white"}`}>
          4-Point Check
        </button>
        <button data-testid="qa-help" onClick={() => navigate("/help")}
          className="w-8 h-8 rounded-full bg-cyan text-ink flex items-center justify-center shrink-0">
          <HelpCircle size={16} />
        </button>
      </div>

      {/* restart */}
      <button data-testid="qa-restart" onClick={handleRestart}
        className="w-full mt-6 py-4 rounded-2xl bg-gradient-to-r from-cyan to-blue-600 text-ink font-heading font-bold text-base tracking-wide shadow-lg shadow-cyan/25 active:scale-[0.99] transition-transform">
        Restart
      </button>

      <p className="text-center font-mono text-[10px] text-slate-500 mt-4">
        Readings come from Visual Scan or manual entry. Tap <span className="text-cyan">Body Align</span> to type values.
      </p>

      {/* bottom tab bar */}
      <div className="fixed bottom-0 inset-x-0 z-40 bg-ink/90 backdrop-blur-xl border-t border-steel">
        <div className="max-w-md mx-auto flex items-stretch px-2 py-2">
          <BottomTab icon={Crosshair} label="Quick Align" active={!isCamber} onClick={() => setView("toe")} testId="tab-quick" />
          <BottomTab icon={SlidersHorizontal} label="Body Align" onClick={() => navigate("/calculator")} testId="tab-body" />
          <BottomTab icon={Disc3} label="Camber" active={isCamber} onClick={() => setView("camber")} testId="tab-camber" />
          <BottomTab icon={Wrench} label="Toolbox" onClick={() => navigate("/specs")} testId="tab-toolbox" />
        </div>
      </div>
    </div>
  );
}
